/**
 * ONE relation collection — the state, the rows and the menus behind every
 * relationship grid on a record (`RelationBand`, `RelationCascade`).
 *
 * ── why this file exists ────────────────────────────────────────────────────
 * Both hosts carried the SAME block, line for line: menu state, a filter column
 * and value, hidden columns, a local sort over the governed one, the filtered
 * and sorted rows, and four popovers. They had already drifted — the Cascade's
 * column list marked a shown column with the words "Yes - " where the Band drew
 * a tick, and only the Band reset its sort and filter when the view changed.
 * Two copies of one behaviour is how a fix lands in one of them.
 *
 * ── the toolbar went, the controls did not (owner, 2026-09-22) ─────────────
 * "Those relation grid buttons. Not sure why they are needed, they could be
 * coming from the column settings next to the column header option menu."
 * Filter, Columns and Sort were a second route to what each column's ⋮ menu
 * already offered. So a relation grid's controls are its COLUMNS now:
 *
 *   · sort    — click the header, or Sort ascending / descending in its menu;
 *               the header draws ↑/↓ and announces `aria-sort`;
 *   · filter  — "Filter…" opens the value field AT that column, and the active
 *               filter shows as a removable pill under the bar and a funnel on
 *               the header — a filtered grid never looks unfiltered;
 *   · columns — "Columns…" is in every column's menu, the record column's
 *               included, so a hidden column can always be brought back;
 *   · group   — "Group by" on a column with a fixed set of values, where the
 *               relation's read is COMPLETE (ADR F-84 §2);
 *   · reset   — "Reset to saved" in the view settings ⋮ (#2375; it was "More").
 *
 * ── each TAB keeps its own state (#2375 §9.4) ──────────────────────────────
 * The key used to RESET everything whenever it changed, so switching from
 * Price lists to Subscriptions and back lost the search, the filter and the
 * sort. It now SELECTS a remembered state: each relationship (and view) keeps
 * its own for the session. None of it is durable and none of it is keyed by a
 * record — the durable nested choice is the saved VIEW, shared across parent
 * records (`useNestedViewChoice`, ADR F-84 §4).
 *
 * ── sorting sorts what the column shows ────────────────────────────────────
 * The sort compared `record[key]`. The record column's key is `__record` and
 * an edge attribute lives on the EDGE, so "Sort ascending" on either compared
 * empty strings and did nothing — a control that looked like it worked.
 *
 * ── the columns are the ones the grid DRAWS ────────────────────────────────
 * An edge slot's grid draws the relationship's own attributes and its validity,
 * not the far entity's default columns — yet Columns, Hide column and the
 * filter list all offered those default columns there, so hiding one changed
 * nothing on screen. The candidate columns now come from the slot.
 */
import { useCallback, useMemo, useReducer, useRef, useState, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { parseSortSpec } from '@solum/sdk'
import { formatMoneyField } from '@solum/format'
import {
  booleanGroupDomain, groupKeyOf, groupLabelOf, groupRowsInMemory, optionGroupDomain, type GroupDomain,
  collectionIsModified, collectionReducer, encodeFilterParam, matchesCollectionText, relationshipCollection,
  resolveCollectionGrouping, selectCollectionGroupBy, selectCollectionScope, transientCollectionKey,
  type CollectionScopeState, type CollectionViewDefaults, type ScopedCollectionState,
} from '@solum/experience'
import { AnchoredSurface, Button, GridFilterPills, Input, Select, optionLabel, clampColumnWidth, togglePinnedColumn, type GridGrouping } from '@solum/ui'
import {
  PAGE_SIZE, displayLabel, useEntityIdentities, useEntityStatusMachine, useReadRoute, useRecordIdentities,
  useSemantics, useRefLabels, useReferenceTargets,
} from '../data/queries'
import { resolveColumnFilterField } from '../list/columnFilterField'
import { relationRead, type RelationSlot } from './relations'
import type { RelationRow, RelationRows } from './useRelations'
import s from './RelationBand.module.css'

export type RelationSortTerm = { key: string; direction: 'asc' | 'desc' }
export type RelationFilterTerm = { column: string; value: string }
type Menu = { kind: 'filter' | 'columns'; anchor: HTMLElement }

export type RelationView = {
  id: string; name?: string; columns?: unknown; sort_spec?: unknown; group_by?: string | null
}

/** The column name a relation grid uses for the far record itself. */
export const RECORD_COLUMN = '__record'

/** What a column SHOWS for a row, which is what it sorts and filters by. */
export function relationValue(
  row: RelationRow, key: string,
  recordLabel: (row: RelationRow) => string = (r) => displayLabel(r.record),
): string {
  const { record, edge } = row
  if (key === RECORD_COLUMN) return recordLabel(row)
  if (key === '__validity') return String(edge?.valid_from ?? '')
  if (key in record) return String(record[key] ?? '')
  const attributes = edge?.attributes && typeof edge.attributes === 'object'
    ? edge.attributes as Record<string, unknown>
    : {}
  return String(attributes[key] ?? '')
}

/** The ListView's governed text, shared by cells and local collection controls. */
export function useRelationValueText(entity: string, rows: RelationRow[]) {
  const semantics = useSemantics(entity)
  const machine = useEntityStatusMachine(entity)
  const targets = useReferenceTargets(entity)
  const records = useMemo(() => rows.map((row) => row.record), [rows])
  const refLabel = useRefLabels(entity, undefined, records)
  const fields = useMemo(() => new Map((semantics.data?.fields ?? []).map((field) => [field.column_name, field])), [semantics.data])
  return useCallback((row: RelationRow, column: string): string => {
    const value = row.record[column]
    if (targets.byColumn.has(column)) return refLabel(column, value, '—', row.record)
    const field = fields.get(column)
    if (field?.kind === 'ref') return '—'
    if (field?.kind === 'money') return formatMoneyField(value, row.record.currency ?? field.format ?? null) || '—'
    const filterField = resolveColumnFilterField({ column, fields, machine: machine.data ?? null })
    if (filterField.kind === 'option' && filterField.options?.length) return optionLabel(value, filterField.options)
    return value == null || value === '' ? '—' : String(value)
  }, [fields, machine.data, refLabel, targets.byColumn])
}

/** The columns a relation grid can draw, besides the record column, in order. */
export function relationColumns(
  slot: RelationSlot | null | undefined, activeView: RelationView | undefined, defaultColumns: string[],
): string[] {
  if (slot?.storage === 'edge') return [...slot.attributes.map((attribute) => attribute.key), '__validity']
  const declared = Array.isArray(activeView?.columns)
    ? activeView.columns.filter((column): column is string => typeof column === 'string')
    : []
  return declared.length > 0 ? declared.slice(0, 4) : defaultColumns
}

/**
 * "OPEN FULL LIST" (#2915, ROOT MB-Opera #91 c.117150) — where a nested grid's
 * rows continue as the far entity's own pageable, searchable list.
 *
 * A relation is read WHOLE up to one read's cap, so a nested grid cannot page
 * or search past it. The far entity's `/e/$entity` list can, and the scope that
 * makes it THIS record's rows is exactly the relation's own read: every
 * equality of `relationRead(slot, recordId).where` — the fk column and, on a
 * discriminated fk, its type column — as one `eq` leaf in the list's `f`. No
 * id list from the first page, no guessed column, nothing the read did not say.
 *
 * Offered ONLY where that is the truth: a LIST slot (never the record's own
 * outbound field), read through an fk (an edge's rows are the relationship's,
 * and flattening them into the far entity's list would drop the edge), and a far
 * entity the Console can navigate. It is offered below the cap too: it is the
 * route to the list, not a claim that the nested read overflowed. The server
 * stays the authority — a person who edits `f` away gets whatever the far
 * entity's own door answers for that.
 */
export function relationFullList(
  slot: RelationSlot | null | undefined, recordId: string, navigable: (entity: string) => boolean,
): { entity: string; f: string } | null {
  // `relationRead` answers null for a FIELD slot (the record's own outbound fk).
  const read = slot ? relationRead(slot, recordId) : null
  if (read?.via !== 'fk' || !navigable(read.entity)) return null
  const f = encodeFilterParam(Object.entries(read.where).map(([field, value]) => ({ field, op: 'eq', value })))
  return f ? { entity: read.entity, f } : null
}

interface RelationFilterDraft { column: string | null; value: string }
type RelationCollectionState = CollectionScopeState<RelationFilterDraft>

/**
 * THE NESTED FOOTER'S CONTEXT (#2375 §9.4/§10: "edge · direction · group", on
 * the right). The approved design printed the metamodel's keys
 * (`located_at · out · edge`); the census ruling (#2406 rogue-debt,
 * machine-text) and Codex's review both say people read WORDS: the
 * relationship's registered label, the direction in words with its arrow, and
 * the governed group — omitted when it is the single default group, which
 * would say nothing.
 */
export const RELATION_DIRECTION_WORDS = {
  out: { words: 'outgoing', arrow: '→' },
  in: { words: 'incoming', arrow: '←' },
  both: { words: 'both ways', arrow: '↔' },
} as const
export function relationFooterContext(
  slot: Pick<RelationSlot, 'label' | 'direction'>, group: string | null | undefined, defaultGroup: string,
): { relationship: string; direction: string; arrow: string; group: string | null } {
  const d = RELATION_DIRECTION_WORDS[slot.direction]
  return {
    relationship: slot.label,
    direction: d.words,
    arrow: d.arrow,
    group: group && group !== defaultGroup ? group : null,
  }
}

/* THE SESSION (#2375 §9.4 "State is per tab"). A nested grid's state lives for
   the session under its PATH — `<parentGridPath>/<entity:id>#<slot>` — so it
   survives switching tabs and collapsing the row it hangs from, and a second
   parent record's grid for the same relationship keeps its own. Transient by
   design: nothing here is persisted or shared with another person. */
let SESSION: ScopedCollectionState<RelationFilterDraft> = {}
/** Test seam: the store is module-level on purpose; a suite starts it clean. */
export function resetRelationCollectionSession(): void { SESSION = {} }

export function useRelationCollection({
  related, slot, recordId, defaultColumns, activeView, resetKey, sessionKey, labelOf,
}: {
  related: RelationRows
  slot: RelationSlot | null | undefined
  /** The record these rows hang from — explicit, never parsed out of `sessionKey`. */
  recordId: string
  /** The child entity's own default columns, used when the view declares none. */
  defaultColumns: string[]
  activeView: RelationView | undefined
  /** Selects this tab's remembered state — each relationship keeps its own. */
  resetKey: string
  /** This grid's PATH — the parent grid's path and the row it hangs from. */
  sessionKey: string
  labelOf: (column: string) => string
}) {
  const storeKey = transientCollectionKey({ path: [sessionKey], viewId: resetKey })
  const defaults = useMemo<CollectionViewDefaults<RelationFilterDraft>>(() => ({
    viewId: activeView?.id ?? resetKey,
    sort: [],
    filters: { column: null, value: '' },
    groupBy: activeView?.group_by ?? null,
  }), [activeView?.group_by, activeView?.id, resetKey])
  const [, rendered] = useReducer((n: number) => n + 1, 0)
  const state = selectCollectionScope(SESSION, storeKey, defaults)
  const patch = useCallback((next: Partial<RelationCollectionState>) => {
    SESSION = collectionReducer(SESSION, { type: 'patch', scope: storeKey, defaults, patch: next })
    rendered()
  }, [defaults, storeKey])
  const { search } = state
  const { column: filterColumn, value: filterValue } = state.filters
  const localSort = state.sort[0] ?? null
  const hiddenColumns = useMemo(() => new Set(state.hiddenColumns), [state.hiddenColumns])
  const setSearch = useCallback((value: string) => patch({ search: value }), [patch])

  const [menu, setMenu] = useState<Menu | null>(null)
  /* Which opening the value field has already been focused for. Opened from a
     column's menu, the field is what the person came for — but `autoFocus`
     lost to that menu closing in the same render, and a ref that focused on
     every render would snatch focus back from the column select. Once per
     opening. */
  const focusedFor = useRef<HTMLElement | null>(null)

  const reset = () => {
    SESSION = collectionReducer(SESSION, { type: 'reset', scope: storeKey, defaults })
    rendered()
  }

  /* THE RECORD COLUMN SHOWS THE GOVERNED TITLE, so that is what it filters,
     sorts and searches by. It used `displayLabel(record)`, which for an address
     is "Record unavailable" — the grid read "Noorderlaan 98, Antwerpen" and a
     filter for "Antw" on that very column matched nothing. The same rule the
     grid's own cell uses: the identity title where it resolves, else the row's
     own name. */
  const refs = useMemo(
    () => (slot ? related.rows.map(({ record }) => ({ type: slot.otherEntity, id: String(record.id) })) : []),
    [related.rows, slot])
  const identities = useRecordIdentities(refs)
  const recordLabel = useCallback((row: RelationRow) => {
    if (!slot) return displayLabel(row.record)
    const identity = identities.identity(slot.otherEntity, String(row.record.id))
    return identity.resolved ? identity.title : displayLabel(row.record)
  }, [identities, slot])

  const baseColumns = useMemo(
    () => relationColumns(slot, activeView, defaultColumns),
    [slot, activeView, defaultColumns])
  const visibleColumns = useMemo(() => {
    const visible = baseColumns.filter((column) => !hiddenColumns.has(column))
    return visible.length > 0 ? visible : baseColumns.slice(0, 1)
  }, [baseColumns, hiddenColumns])

  const governedSort = useMemo(
    () => parseSortSpec(Array.isArray(activeView?.sort_spec) ? activeView.sort_spec : undefined)[0] ?? null,
    [activeView?.sort_spec])
  const sort: RelationSortTerm | null = localSort ?? governedSort
  /* The column the select SHOWS is the one the filter USES. It defaulted to
     showing the first column while holding `null`, and a value typed without
     touching the select filtered nothing at all. */
  const filter: RelationFilterTerm | null = useMemo(
    () => (filterValue.trim() ? { column: filterColumn ?? RECORD_COLUMN, value: filterValue.trim() } : null),
    [filterColumn, filterValue])

  /* ── GROUPED ROWS (ADR F-84 §2) ────────────────────────────────────────────
     A relation's rows are read WHOLE, up to the read's cap. Grouping them in
     memory is exact only while the read is complete, so a relation at its cap
     never offers "Group by", and a saved view that groups one says so instead
     of grouping a partial read. Only the far RECORD's own columns group; an
     edge's attributes are the relationship's, not the record's. */
  const otherEntity = slot?.otherEntity ?? ''
  const recordValueText = useRelationValueText(otherEntity, related.rows)
  const valueText = useCallback((row: RelationRow, column: string) =>
    column === RECORD_COLUMN || slot?.storage === 'edge'
      ? relationValue(row, column, recordLabel)
      : recordValueText(row, column), [recordLabel, recordValueText, slot?.storage])
  const semantics = useSemantics(otherEntity)
  const machine = useEntityStatusMachine(otherEntity)
  const farRoute = useReadRoute(otherEntity)
  const fields = useMemo(() => new Map((semantics.data?.fields ?? []).map((f) => [f.column_name, f])), [semantics.data])
  // A governed far side (subject doors, module door) is read in its provider's page.
  const readLimit = farRoute.pageLimit ?? PAGE_SIZE
  const adapter = relationshipCollection({
    rowCount: related.rows.length,
    limit: readLimit,
    settled: !related.isLoading,
    failed: Boolean(related.isError),
    reason: 'it has more rows than one read returns',
  })
  const domainFor = useCallback((column: string): GroupDomain | null => {
    if (!slot || slot.storage === 'edge' || column === RECORD_COLUMN || column === '__validity') return null
    const field = resolveColumnFilterField({ column, fields, machine: machine.data ?? null })
    if (field.kind === 'option' && field.options && field.options.length > 0) return optionGroupDomain(column, field.options)
    if (String((fields.get(column) as { kind?: string } | undefined)?.kind ?? '') === 'boolean') return booleanGroupDomain(column)
    return null
  }, [slot, fields, machine.data])
  const groupBy = selectCollectionGroupBy(state, defaults)
  const groupDomain = groupBy ? domainFor(groupBy) : null
  const groupingDecision = resolveCollectionGrouping({
    adapter,
    requestedColumn: groupBy,
    enumerable: groupDomain != null,
  })
  const groupSupported = groupingDecision.kind === 'supported'
  /* A read still in flight, or one that FAILED, says nothing about whether the
     relation is complete: neither groups, and neither claims it cannot. */
  const groupUnsupported = groupingDecision.kind === 'unsupported'
    && adapter.completeness.kind !== 'pending' && adapter.completeness.kind !== 'failed'

  const rows = useMemo(() => {
    const needle = filter?.value.toLocaleLowerCase() ?? ''
    const kept = related.rows.filter((row) => {
      const matchesSearch = matchesCollectionText(search, recordLabel(row), ...(slot?.storage === 'edge' ? Object.values(row.record) : baseColumns.map((column) => valueText(row, column))))
      const matchesFilter = !filter || valueText(row, filter.column).toLocaleLowerCase().includes(needle)
      return matchesSearch && matchesFilter
    })
    if (!sort) return kept
    return [...kept].sort((left, right) => {
      const compared = valueText(left, sort.key).localeCompare(valueText(right, sort.key))
      return sort.direction === 'desc' ? -compared : compared
    })
  }, [baseColumns, filter, recordLabel, related.rows, search, slot?.storage, sort, valueText])
  /* Grouping follows search and filter (it groups what is SHOWN), and the
     remaining sort orders rows inside each group. The totals are the grouped
     rows' own — this read is complete, so they are the relation's. */
  const grouped = useMemo(() => groupSupported && groupDomain
    ? groupRowsInMemory(rows, groupDomain, (row) => row.record[groupDomain.column],
      sort?.key === groupDomain.column && sort.direction === 'desc' ? 'desc' : 'asc')
    : null, [groupSupported, groupDomain, rows, sort])
  const shownRows = grouped ? grouped.rows : rows
  const visibleRelated = useMemo(() => ({ ...related, rows: shownRows }), [related, shownRows])

  /* The header click cycles ascending → descending → this person's choice gone
     (back to the view's own order), so a sort can be undone where it was made. */
  const toggleSort = (key: string) => {
    const active = localSort ?? null
    const next = !active || active.key !== key ? { key, direction: 'asc' as const }
      : active.direction === 'asc' ? { key, direction: 'desc' as const } : null
    patch({ sort: next ? [next] : [] })
  }

  const grouping: GridGrouping | undefined = grouped && groupDomain ? {
    column: groupDomain.column,
    columnLabel: labelOf(groupDomain.column),
    /* BandGrid's rows carry the relation row under `relation` (asBandRow). */
    keyOf: (row) => groupKeyOf(groupDomain,
      (row as { relation?: RelationRow }).relation?.record[groupDomain.column]),
    labelOf: (key) => groupLabelOf(groupDomain, key),
    totalOf: (key) => grouped.totals.get(key) ?? 0,
  } : undefined

  const grid = {
    valueText,
    columnKeys: visibleColumns,
    sort,
    filter,
    grouping,
    onToggleSort: toggleSort,
    onSortColumn: (key: string, direction: 'asc' | 'desc') => patch({ sort: [{ key, direction }] }),
    onFilterColumn: (key: string, anchor: HTMLElement) => {
      patch({ filters: { ...state.filters, column: key } })
      setMenu({ kind: 'filter', anchor })
    },
    onHideColumn: (key: string) => patch({ hiddenColumns: [...new Set([...state.hiddenColumns, key])] }),
    onChooseColumns: (anchor: HTMLElement) => setMenu({ kind: 'columns', anchor }),
    /* Offered only where this read can group exactly (ADR F-84 §2). */
    groupOf: (key: string) => (adapter.capabilities.grouping && domainFor(key) != null
      ? { active: groupBy === key, onToggle: () => patch({ groupBy: groupBy === key ? null : key }) }
      : undefined),
    /* Narrowed by this person, so an empty grid is "nothing matches", never
       "none yet" — the relationship still has its rows. */
    narrowed: Boolean(search.trim()) || filter != null,
    /* Row pins and the column layout, this tab's own (#2375 §9.4, §6.3). */
    pinnedRowIds: state.pinnedRows as string[],
    onPinnedRowIdsChange: (ids: string[]) => patch({ pinnedRows: ids }),
    pinnedColumns: state.pinnedColumns,
    onTogglePinColumn: (key: string) => patch({ pinnedColumns: togglePinnedColumn(state.pinnedColumns, key) }),
    widthOf: (key: string) => state.widths[key],
    onResizeColumn: (key: string, px: number) => patch({ widths: { ...state.widths, [key]: clampColumnWidth(px) } }),
    onResetColumn: (key: string) => {
      const widths = { ...state.widths }
      delete widths[key]
      patch({ widths })
    },
  }

  /* The view-settings ⋮ owns the collection-level act that was "More". */
  const settings = {
    modified: collectionIsModified(state, defaults),
    onReset: reset,
  }

  /* The far entity's navigation eligibility is the one the generic list itself
     reads (`EntityList`): the identity registry's `navigation_eligible`. */
  const entityIdentities = useEntityIdentities()
  const fullList = relationFullList(slot, recordId, (entity) => (entityIdentities.data ?? [])
    .some((identity) => identity.entity_type === entity && identity.navigation_eligible === true))

  /* The applied-filter row's slot is the one place every host of this
     collection already draws — the band's and the section's bars, and the
     record page's bare plate — so the full-list route rides it, after the pills. */
  const filters: ReactNode = (<>
    <GridFilterPills onClearAll={() => patch({ filters: { ...state.filters, value: '' } })} pills={filter ? [{
      key: filter.column,
      column: labelOf(filter.column),
      condition: 'contains',
      value: filter.value,
      onRemove: () => patch({ filters: { ...state.filters, value: '' } }),
      onOpen: (anchor) => setMenu({ kind: 'filter', anchor }),
    }] : []} />
    {fullList && slot ? (
      <Button asChild variant="ghost" size="sm">
        <Link to="/e/$entity" params={{ entity: fullList.entity }} search={{ f: fullList.f }}
          data-testid={`relation-full-list-${slot.id}`}>Open full list</Link>
      </Button>
    ) : null}
  </>)

  const notice: ReactNode = groupUnsupported && groupBy ? (
    <span data-testid="relation-group-unsupported">
      This view groups by {labelOf(groupBy)}, which this relationship cannot group exactly
      {groupDomain ? ` — it has more rows than one read returns` : ' — it is not a field with a fixed set of values'}.
      Shown ungrouped.
    </span>
  ) : undefined

  const close = () => { focusedFor.current = null; setMenu(null) }
  const menus: ReactNode = menu == null ? null : menu.kind === 'filter' ? (
    <AnchoredSurface anchor={menu.anchor} onClose={close} width={300}>
      <div className={s.menuHeading}>Filter</div>
      <Select aria-label="Filter column" value={filterColumn ?? RECORD_COLUMN}
        options={[RECORD_COLUMN, ...visibleColumns].map((key) => ({ value: key, label: labelOf(key) }))}
        onValueChange={(value) => patch({ filters: { ...state.filters, column: value } })} />
      <Input aria-label="Filter value" value={filterValue} placeholder="Contains…"
        ref={(field) => {
          if (!field || focusedFor.current === menu.anchor) return
          focusedFor.current = menu.anchor
          requestAnimationFrame(() => field.focus())
        }}
        onChange={(event) => patch({ filters: { ...state.filters, value: event.currentTarget.value } })} />
      {filterValue && <Button variant="ghost" size="sm" onClick={() => patch({ filters: { ...state.filters, value: '' } })}>Clear filter</Button>}
    </AnchoredSurface>
  ) : (
    <AnchoredSurface anchor={menu.anchor} onClose={close} width={280}>
      <div className={s.menuHeading}>Columns</div>
      {baseColumns.map((key) => {
        const shown = visibleColumns.includes(key)
        /* A toggle says it is one: `aria-pressed`, and a tick glyph for the
           eye. The Cascade's copy spelled the state out as "Yes - " in the
           label, which a screen reader then read as part of the column name. */
        return (
          <Button key={key} variant="ghost" size="sm" className={s.menuItem}
            aria-pressed={shown} disabled={shown && visibleColumns.length === 1}
            icon={<Check size={14} aria-hidden className={s.tick} data-shown={shown} />}
            onClick={() => patch({
              hiddenColumns: shown ? [...new Set([...state.hiddenColumns, key])]
                : state.hiddenColumns.filter((column) => column !== key),
            })}>{labelOf(key)}</Button>
        )
      })}
    </AnchoredSurface>
  )

  /* The column verbs a hidden header can no longer offer (#2375): sort, group and
     the chooser, as the view-settings ⋮ takes them. */
  const columnVerbs = {
    columns: visibleColumns.map((key) => ({ key, label: labelOf(key) })),
    sortActive: sort ? { key: sort.key, direction: sort.direction } : null,
    groupable: visibleColumns.filter((key) => adapter.capabilities.grouping && domainFor(key) != null)
      .map((key) => ({ key, label: labelOf(key) })),
    groupActive: groupBy ?? null,
    onGroup: (key: string | null) => patch({ groupBy: key }),
  }
  return { search, setSearch, visibleRelated, grid, settings, filters, notice, menus, columnVerbs }
}
